import { Button, Col, Modal, Row, Space, Tooltip, Typography } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Prompt, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";
import { ApiNames, deleteSavedListAPI } from "../../api/api";
import ArrowBack from "../../assets/svg/ArrowBack.js";
import { ReactComponent as DeleteIcon } from "../../assets/svg/delete-new-icon.svg";
import { ReactComponent as WarningIcon } from "../../assets/svg/warning-icon-popup.svg";
import { ModalComponent } from "../../component/customComponent/ModalComponent";
import { BLACK, WHITE } from "../../constant/colors";
import { defaultFilters } from "../../constant/filters";
import { ViewType } from "../../constant/general.js";
import { Routes } from "../../constant/routes";
import { fontWeights, sizes } from "../../constant/styles";
import { default as ACTION, default as ACTIONS } from "../../store/action";
import { starFilterList } from "../../api/api";
import { ReactComponent as UserIcon } from "../../assets/svg/user-icon.svg";
import { ReactComponent as InfoSavedList } from "../../assets/svg/info-saved-list.svg";
import { GRAY_300, BLUE, BLUE_HOVER } from "../../constant/colors";
import {
  getUserSelectedColumns,
  selectFilterList,
  selectFilterListStatuses,
  selectFilters,
  selectIsFetching,
  selectIsFilterListFiltersModified,
  selectSorting,
  selectTableColumnsModified,
  selectTableSorterModified,
  selectUser,
  selectAllUsersAndUserGroups,
  selectUserGroupsWithUsers,
  selectTotalPatients
} from "../../store/selector";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage.js";
import SuccessMessage from "../customComponent/customMessages/SuccessMessage.js";
import { ToolBar } from "../customComponent/ToolBar.js";
import { SavedListActionsMenu } from "./SavedListActionsMenu.js";
import SaveNewListButton from "./SaveNewListButton.js";

import { dateTimeFormatter } from "../../utils/date";
import { StarFilterListColumn } from "../customComponent/CommonColumns";
import { ViewersBadge, TypeIconBadge } from "../../utils/savedList.js";

const { Text } = Typography;
const POP_ACTION = "POP";

const BackButtonStyle = styled(Button)`
  background-color: transparent;
  border: none;
  padding: 0px;
  height: 25px;
`;

const StyledSaveButton = styled(Button)`
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.semibold};
  border: 1px solid ${BLUE};
  border-radius: 6px;
  background-color: ${BLUE};
  color: ${WHITE};
  height: 31px;
  width: 140px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${BLUE_HOVER} !important;
    border-color: ${BLUE_HOVER} !important;
    color: ${WHITE} !important;
  }

  &:focus {
    background-color: ${BLUE} !important;
    border-color: ${BLUE} !important;
    box-shadow: 0 0 0 4px rgba(47, 84, 155, 0.3) !important;
    color: ${WHITE} !important;
    outline: none !important;
  }

  &:disabled {
    background-color: ${GRAY_300};
    border-color: ${GRAY_300};
    color: ${WHITE};
    &:hover {
      background-color: ${GRAY_300} !important;
      border-color: ${GRAY_300} !important;
      color: ${WHITE} !important;
    }
  }
`;

const FilterListControls = ({ onViewTypeChange }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const fromSavedBackButton = useRef(false);
  const redirectToLinkRef = useRef(undefined);
  const filterList = useSelector(selectFilterList);
  const filters = useSelector(selectFilters);
  const sorting = useSelector(selectSorting);
  const isFetching = useSelector(selectIsFetching([ApiNames.UpdateFilterList]));
  const user = useSelector(selectUser);
  const filterListFiltersModified = useSelector(selectIsFilterListFiltersModified);
  const tableColumnsModified = useSelector(selectTableColumnsModified);
  const tableSortingModified = useSelector(selectTableSorterModified);
  const filterListStatuses = useSelector(selectFilterListStatuses);
  const userSelectedColumns = useSelector(getUserSelectedColumns);
  const assigneesAndUserGroups = useSelector(selectAllUsersAndUserGroups);
  const userGroupsWithUsers = useSelector(selectUserGroupsWithUsers);
  const totalPatients = useSelector(selectTotalPatients);
  const { viewType } = useParams();
  const setFilters = (filters) => dispatch(ACTIONS.setFilters(filters));
  const setFiltersAct = (filters, apiName, page, sorting) =>
    dispatch(ACTIONS.setFiltersAction(filters, apiName, page, sorting));
  const clearSavedListRelatedDataAct = () => dispatch(ACTIONS.clearSavedListRelatedDataAction());
  const markSavedFilterAsChanged = (filterListID, isModified) =>
    dispatch(ACTIONS.markSavedFilterAsChanged(filterListID, isModified));
  const searchSavedListAct = (page) => dispatch(ACTION.searchSavedListAction(page));

  const isOwner = user?.id === filterList?.user;
  const isModified = tableColumnsModified || filterListFiltersModified || tableSortingModified;
  const canModify = isOwner && isModified;

  const updateFilterListStatusesAct = (filterListID, canModify, showDiscardModal) =>
    dispatch(ACTIONS.updateFilterListStatuses(filterListID, canModify, showDiscardModal));
  const setFormPopupsAct = (visible, isDuplicateMode) => dispatch(ACTIONS.setFormPopups(visible, isDuplicateMode));
  const updateFilterListAct = (newFilterListData, filterListId) =>
    dispatch(ACTIONS.updateFilterListAction(newFilterListData, filterListId));
  const setSavedListViewTypeAct = (savedListViewType) => dispatch(ACTIONS.setSavedListViewType(savedListViewType));
  const setFilterListAct = (filterListData) => dispatch(ACTIONS.setFilterList(filterListData));

  useEffect(() => {
    updateFilterListStatusesAct(filterList?.id, canModify, false);
  }, [filterList?.id, canModify]);

  useEffect(() => {
    setShowDiscardModal(filterListStatuses.showDiscardModal);
  }, [filterListStatuses]);

  const handleBrowserBack = (fromSavedListBack = false) => {
    const redirectToLinkPathParams = redirectToLinkRef.current?.split("/");
    const clearSavedList = !redirectToLinkRef.current || redirectToLinkPathParams[4];

    const backFromCalendarView =
      redirectToLinkRef.current &&
      redirectToLinkPathParams.length > 3 &&
      redirectToLinkPathParams[4] !== history.location.pathname.split("/")[4];
    const listViewPathNotChanged =
      redirectToLinkRef.current && redirectToLinkPathParams[4] === history.location.pathname.split("/")[4];

    if (clearSavedList === ViewType.List.key) {
      setSavedListViewTypeAct(ViewType.List.key);
      setFiltersAct({ ...filters, forceCallApi: true }, ApiNames.Search, 1, sorting);
      if (!backFromCalendarView && !listViewPathNotChanged) {
        history.replace(`${Routes.ROOT}${Routes.SAVEDLISTS}`);
        return;
      }
    }
    if (!clearSavedList && !backFromCalendarView) {
      setFilters(defaultFilters);
      clearSavedListRelatedDataAct();
      markSavedFilterAsChanged(undefined, false);
    }

    if (fromSavedListBack) {
      history.replace(`${Routes.ROOT}${Routes.SAVEDLISTS}`);
    } else {
      redirectToLinkRef.current && !listViewPathNotChanged && history.replace(redirectToLinkRef.current);
    }
    redirectToLinkRef.current = undefined;
  };

  const handleStarChange = async (newStarredValue) => {
    try {
      await starFilterList(filterList?.id, newStarredValue);
      setFilterListAct({ ...filterList, starred: newStarredValue });
    } catch (error) {
      ErrorMessage(t("errorMessages.filterlist_starred"));
    }
  };

  const handleSaveButton = async () => {
    updateFilterListStatusesAct(filterList?.id, canModify, false);

    const newFilterListValues = {
      name: filterList?.name,
      description: filterList?.description,
      isPrivate: filterList?.isPrivate === 1 ? true : false,
      selectedView: filterList?.selectedView,
      params: {
        filters: { ...filters, isOpen: false },
        selectedColumns: userSelectedColumns,
        sorting
      }
    };

    markSavedFilterAsChanged(filterList?.id, false);
    updateFilterListAct(newFilterListValues, filterList?.id);
  };

  useEffect(() => {
    if (filterList?.name && showDiscardModal) {
      Modal.confirm({
        className: "formPopupsModal",
        centered: true,
        okText: t("yes_sure"),
        cancelText: t("back"),
        icon: <WarningIcon />,
        title: t("savedList.discard_modal.title"),
        content: t("savedList.discard_modal.content"),
        onOk() {
          redirectToLinkRef.current = fromSavedBackButton.current
            ? `${Routes.ROOT}${Routes.SAVEDLISTS}`
            : redirectToLinkRef.current;
          handleBrowserBack(fromSavedBackButton.current);
          setShowDiscardModal(false);
        },
        onCancel() {
          setShowDiscardModal(false);
          redirectToLinkRef.current = undefined;
          fromSavedBackButton.current = false;
        }
      });
    }
  }, [filterList, showDiscardModal]);

  return (
    <div style={{ backgroundColor: WHITE }}>
      <Prompt
        message={(location, action) => {
          const pathParams = location.pathname.split("/");
          if (action === POP_ACTION) {
            if (location.pathname.includes(ViewType.SavedList.key) && !pathParams[3])
              fromSavedBackButton.current = true;
            else if (location.pathname?.split("/")[4] === ViewType.List.key)
              redirectToLinkRef.current = location.pathname;
            if (canModify && (pathParams.length === 3 || pathParams[4] !== ViewType.List.key)) {
              setShowDiscardModal(true);
              return false;
            } else {
              handleBrowserBack(fromSavedBackButton.current);
              return true;
            }
          } else if (action !== POP_ACTION && !redirectToLinkRef.current && pathParams[4] !== ViewType.Calendar.key) {
            redirectToLinkRef.current = location.pathname;
            if (canModify && (pathParams.length === 3 || pathParams[4] !== ViewType.List.key)) {
              setShowDiscardModal(true);
              return false;
            } else {
              history.replace(redirectToLinkRef.current);
              redirectToLinkRef.current = null;
            }
            redirectToLinkRef.current = location.pathname;
            handleBrowserBack();
            return true;
          }
          return true;
        }}
      />
      <Row style={{ padding: "0 50px 0 50px" }}>
        <Col span={20}>
          <Space direction="vertical" size={0}>
            <Space direction="horizontal" style={{ height: "30px" }}>
              <Tooltip title={t("back")}>
                <BackButtonStyle
                  onClick={() => {
                    fromSavedBackButton.current = true;
                    if (canModify) {
                      setShowDiscardModal(true);
                    } else {
                      handleBrowserBack(true);
                    }
                  }}
                  type="text"
                >
                  <ArrowBack />
                </BackButtonStyle>
              </Tooltip>
            </Space>
            <ToolBar t={t} history={history} user={user} onViewTypeChange={onViewTypeChange} viewType={viewType} />
            <Space direction="vertical" size={0} style={{ paddingTop: "20px", width: "100%" }}>
              <Space align="center" style={{ width: "100%" }}>
                <StarFilterListColumn
                  page={1}
                  record={filterList}
                  isFilterListView={true}
                  onStarChange={handleStarChange}
                />
                <Text
                  style={{
                    fontSize: sizes.medium_xlarge,
                    color: BLACK,
                    fontWeight: fontWeights.semibold,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {filterList?.name}
                </Text>

                {filterList?.description && (
                  <Tooltip
                    color={WHITE}
                    title={filterList?.description}
                    overlayStyle={{
                      maxWidth: "500px"
                    }}
                    overlayInnerStyle={{
                      color: BLACK,
                      fontSize: sizes.medium,
                      fontWeight: fontWeights.regular
                    }}
                  >
                    <InfoSavedList
                      id="list_screen_details"
                      style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                    />
                  </Tooltip>
                )}

                <TypeIconBadge record={filterList} />

                <ViewersBadge
                  record={filterList}
                  assigneesAndUserGroups={assigneesAndUserGroups}
                  userGroupsWithUsers={userGroupsWithUsers}
                  t={t}
                />
              </Space>
              <Space
                style={{
                  fontSize: sizes.small,
                  fontWeight: fontWeights.regular,
                  gap: "10px"
                }}
              >
                <Text style={{ color: BLACK }}>
                  {t("savedList.description", {
                    view: "Orders"
                  })}
                </Text>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <UserIcon />
                  <Text style={{ color: BLACK }}>
                    {t("savedList.created_by")} {filterList?.username}
                  </Text>
                </div>

                <Text style={{ color: BLACK }}>
                  {t("savedList.last_update_lbl")} {dateTimeFormatter(filterList?.updatedAt)}
                </Text>
              </Space>
            </Space>
          </Space>
        </Col>
        <Col
          span={4}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "end"
          }}
        >
          <Space direction="vertical" align="end" size={10}>
            <Space direction="horizontal" size={16}>
              {isOwner && (
                <StyledSaveButton
                  id="list_screen_save"
                  type="primary"
                  disabled={!canModify}
                  loading={isFetching}
                  onClick={handleSaveButton}
                >
                  {t("filter_list_view.filter_list_top_section.save")}
                </StyledSaveButton>
              )}
              {user?.id && filterList?.user && (
                <SaveNewListButton
                  disabled={false}
                  id="Save_New_Saved_List"
                  setFormPopupsAct={setFormPopupsAct}
                  isOwner={isOwner}
                />
              )}
              <SavedListActionsMenu
                record={filterList}
                recordCount={totalPatients}
                source="lists_screen"
                deleteAction={() => {
                  setShowDeleteModal(true);
                }}
              />
            </Space>
          </Space>
        </Col>

        {canModify && (
          <Space
            direction="horizontal"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "end",
              width: "100%",
              marginTop: "5px",
              paddingBottom: "15px",
              position: "absolute",
              right: 55,
              top: 145,
              zIndex: 1000
            }}
          >
            <Text
              style={{
                fontSize: sizes.small,
                color: BLACK,
                fontWeight: fontWeights.regular
              }}
            >
              {t("filter_list_view.filter_list_top_section.unsaved_changes")}
            </Text>
          </Space>
        )}
      </Row>

      <ModalComponent
        open={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
        }}
        okText={t("ok_text")}
        onOk={async () => {
          try {
            const deleteListRes = await deleteSavedListAPI(filterList?.id);
            if (deleteListRes) {
              searchSavedListAct(1);
              history.push(`${Routes.ROOT}${ViewType.SavedList.key}`);
              SuccessMessage(t("savedList.confirm_modal.success_message"));
            }
          } catch (error) {
            ErrorMessage(t("savedList.confirm_modal.error_message"));
          }
        }}
      >
        <Space direction="vertical">
          <DeleteIcon style={{ width: "48px" }} />
          <Text className="title">{t("savedList.confirm_modal.title")}</Text>
          <Text className="content">
            {t("savedList.confirm_modal.content", {
              listName: filterList.name
            })}
          </Text>
        </Space>
      </ModalComponent>
    </div>
  );
};

export { FilterListControls };
